import React, { useState } from 'react';
import {
  BookOpen,
  CheckCircle2,
  ExternalLink,
  ShieldAlert,
  Search,
  UserCheck,
  Code2,
  Calculator,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle,
  Award,
  Sparkles,
} from 'lucide-react';
import { AppView, ContentEntry } from '../../types';
import { seoCmsEngine } from '../../services/seoCmsEngine';

interface Props {
  onNavigateToTool?: (view: AppView) => void;
}

export const FinancialGuidesHub: React.FC<Props> = ({ onNavigateToTool }) => {
  const [guides] = useState<ContentEntry[]>(seoCmsEngine.getGuides());
  const [selectedGuide, setSelectedGuide] = useState<ContentEntry | null>(guides[0] || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCluster, setSelectedCluster] = useState<string>('ALL');
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const clusters = ['ALL', 'Salary & Income Tax', 'International Transfers', 'Loans & Mortgages'];

  const filteredGuides = guides.filter((g) => {
    const matchesSearch =
      g.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.topicCluster.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCluster = selectedCluster === 'ALL' || g.topicCluster === selectedCluster;
    return matchesSearch && matchesCluster;
  });

  const activeAuthor = selectedGuide ? seoCmsEngine.getAuthorById(selectedGuide.authorId) : null;
  const activeReviewer = selectedGuide?.reviewerId ? seoCmsEngine.getAuthorById(selectedGuide.reviewerId) : null;

  return (
    <div className="space-y-8 pb-12">
      {/* Header Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-500/30">
            <Award className="w-3.5 h-3.5" />
            E-E-A-T Certified & YMYL Financial Knowledge Engine
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            YamooPay Global Financial Guides & Methodology
          </h1>
          <p className="text-slate-300 text-base leading-relaxed">
            Expert-reviewed, mathematically grounded financial intelligence. Every guide is authored by certified CPAs & CFAs, backed by Tier 1 official regulators, and updated in real time.
          </p>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative w-full sm:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search guides, tax laws, FX corridors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          {clusters.map((cluster) => (
            <button
              key={cluster}
              onClick={() => setSelectedCluster(cluster)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                selectedCluster === cluster
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cluster}
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid: Guide List + Selected Guide Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Guide Cards (5 Cols) */}
        <div className="lg:col-span-5 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 px-1">
            Verified Guides ({filteredGuides.length})
          </h2>

          <div className="space-y-3">
            {filteredGuides.map((guide) => {
              const isSelected = selectedGuide?.id === guide.id;
              const author = seoCmsEngine.getAuthorById(guide.authorId);

              return (
                <div
                  key={guide.id}
                  onClick={() => setSelectedGuide(guide)}
                  className={`p-5 rounded-xl border cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-emerald-50/50 border-emerald-500 ring-1 ring-emerald-500/50 shadow-md'
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                    <span className="font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded">
                      {guide.topicCluster}
                    </span>
                    <span className="flex items-center gap-1 text-slate-400">
                      <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
                      {guide.riskLevel} YMYL
                    </span>
                  </div>

                  <h3 className="font-bold text-slate-900 text-base leading-snug mb-2">
                    {guide.title}
                  </h3>

                  <p className="text-slate-600 text-xs line-clamp-2 mb-3">
                    {guide.summary}
                  </p>

                  <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 pt-3">
                    <span className="font-medium text-slate-700">
                      {author ? author.name : 'YamooPay Expert'}
                    </span>
                    <span>Updated {guide.updatedAt.split('T')[0]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: Active Guide Detailed Reader & Trust Layer (7 Cols) */}
        <div className="lg:col-span-7">
          {selectedGuide ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
              {/* Top Meta Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-800 text-xs font-bold">
                    {selectedGuide.topicCluster}
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-medium">
                    Locale: {selectedGuide.locale.toUpperCase()}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSchemaModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-medium"
                  >
                    <Code2 className="w-3.5 h-3.5 text-slate-500" />
                    Schema.org JSON-LD
                  </button>

                  {selectedGuide.embeddedToolId && (
                    <button
                      onClick={() => onNavigateToTool?.(selectedGuide.embeddedToolId!)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-semibold shadow-sm"
                    >
                      <Calculator className="w-3.5 h-3.5" />
                      Launch Calculator
                    </button>
                  )}
                </div>
              </div>

              {/* Title & Summary */}
              <div className="space-y-3">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  {selectedGuide.title}
                </h1>
                <p className="text-slate-600 text-sm leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {selectedGuide.summary}
                </p>
              </div>

              {/* E-E-A-T Author & Reviewer Card */}
              <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-xl p-4 sm:p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-800 uppercase tracking-wider">
                    <UserCheck className="w-4 h-4 text-emerald-600" />
                    Editorial & Technical Governance
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-white px-2 py-0.5 rounded border border-emerald-200">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    YMYL Risk: {selectedGuide.riskLevel}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  {/* Author */}
                  {activeAuthor && (
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-emerald-100">
                      {activeAuthor.profileImage && (
                        <img
                          src={activeAuthor.profileImage}
                          alt={activeAuthor.name}
                          className="w-10 h-10 rounded-full object-cover border border-slate-200"
                        />
                      )}
                      <div className="space-y-0.5">
                        <div className="text-slate-400 text-[10px] uppercase font-bold">Authored By</div>
                        <div className="font-bold text-slate-900">{activeAuthor.name}</div>
                        <div className="text-slate-500 text-[11px] line-clamp-1">{activeAuthor.role}</div>
                        <div className="text-emerald-700 font-semibold text-[10px]">
                          ✓ {activeAuthor.credentials[0]}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reviewer */}
                  {activeReviewer && (
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-emerald-100">
                      {activeReviewer.profileImage && (
                        <img
                          src={activeReviewer.profileImage}
                          alt={activeReviewer.name}
                          className="w-10 h-10 rounded-full object-cover border border-slate-200"
                        />
                      )}
                      <div className="space-y-0.5">
                        <div className="text-slate-400 text-[10px] uppercase font-bold">Reviewed By</div>
                        <div className="font-bold text-slate-900">{activeReviewer.name}</div>
                        <div className="text-slate-500 text-[11px] line-clamp-1">{activeReviewer.role}</div>
                        <div className="text-emerald-700 font-semibold text-[10px]">
                          ✓ {activeReviewer.credentials[0]}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Guide Content Body (Markdown styled) */}
              <div className="prose prose-slate max-w-none text-slate-800 text-sm leading-relaxed space-y-4 pt-2">
                <div className="whitespace-pre-wrap font-sans space-y-3">
                  {selectedGuide.bodyMarkdown}
                </div>
              </div>

              {/* Correction Notes if any */}
              {selectedGuide.corrections && selectedGuide.corrections.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-amber-800">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Correction & Revision Log
                  </div>
                  {selectedGuide.corrections.map((corr) => (
                    <div key={corr.id} className="text-xs text-amber-900 space-y-1 bg-white/80 p-2.5 rounded border border-amber-100">
                      <div className="flex items-center justify-between text-[11px] text-amber-700 font-medium">
                        <span>Corrected by {corr.correctedBy}</span>
                        <span>{corr.correctedAt.split('T')[0]}</span>
                      </div>
                      <p className="font-semibold">{corr.reason}</p>
                      <div className="text-[11px] text-slate-600">
                        <span className="line-through text-red-600 mr-2">{corr.previousValue}</span>
                        <span className="text-emerald-700 font-medium">→ {corr.newValue}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tier 1 Official Sources Section */}
              {selectedGuide.sources && selectedGuide.sources.length > 0 && (
                <div className="border-t border-slate-200 pt-5 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-slate-600" />
                    Official Regulatory Data Sources ({selectedGuide.sources.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedGuide.sources.map((src) => (
                      <div
                        key={src.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs gap-2"
                      >
                        <div className="space-y-0.5">
                          <div className="font-semibold text-slate-800">{src.citationLabel}</div>
                          <div className="text-[11px] text-slate-500">
                            Tier: <span className="font-bold text-emerald-700">{src.tier}</span> • Verified: {src.verifiedAt.split('T')[0]}
                          </div>
                        </div>
                        <a
                          href={src.referenceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-semibold text-xs shrink-0"
                        >
                          Source Link <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Accordions */}
              {selectedGuide.faqs && selectedGuide.faqs.length > 0 && (
                <div className="border-t border-slate-200 pt-5 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                    Frequently Asked Questions
                  </h3>
                  <div className="space-y-2">
                    {selectedGuide.faqs.map((faq, idx) => {
                      const isOpen = openFaqIndex === idx;
                      return (
                        <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50/50">
                          <button
                            onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                            className="w-full flex items-center justify-between p-3.5 text-left text-xs font-bold text-slate-800 hover:bg-slate-100 transition-colors"
                          >
                            <span>{faq.question}</span>
                            {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                          </button>
                          {isOpen && (
                            <div className="p-3.5 bg-white border-t border-slate-200 text-xs text-slate-600 leading-relaxed">
                              {faq.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
              Select a guide on the left to read full methodology & analysis.
            </div>
          )}
        </div>
      </div>

      {/* Schema.org JSON-LD Modal */}
      {showSchemaModal && selectedGuide && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 text-slate-100 rounded-2xl w-full max-w-2xl border border-slate-800 shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Code2 className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-base text-white">Schema.org JSON-LD Structured Data</h3>
              </div>
              <button
                onClick={() => setShowSchemaModal(false)}
                className="text-slate-400 hover:text-white text-xs font-bold px-2 py-1 rounded bg-slate-800"
              >
                Close
              </button>
            </div>

            <p className="text-xs text-slate-400">
              Validated structured data output matching on-page content 1:1 for Google Search Central rich snippet eligibility.
            </p>

            <pre className="bg-slate-950 p-4 rounded-xl text-xs font-mono text-emerald-300 overflow-x-auto max-h-96 border border-slate-800">
              {JSON.stringify(seoCmsEngine.generateJsonLdForGuide(selectedGuide), null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
